(function (root, factory) {
  var api = factory();
  root.CartBundleCore = api;
  if (typeof module === 'object' && module.exports) module.exports = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  function finiteNumber(value, fallback) {
    var number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function sameId(left, right) {
    return left !== null && left !== undefined && right !== null && right !== undefined && String(left) === String(right);
  }

  function deriveDeliveryState(totalPrice, threshold, enabled) {
    var safeThreshold = finiteNumber(threshold, 0);
    var safeTotal = Math.max(0, finiteNumber(totalPrice, 0));
    var active = enabled === true && safeThreshold > 0;
    if (!active) return { enabled: false, qualified: false, remaining: 0, progress: 0 };

    return {
      enabled: true,
      qualified: safeTotal >= safeThreshold,
      remaining: Math.max(0, safeThreshold - safeTotal),
      progress: Math.min(100, (safeTotal / safeThreshold) * 100),
    };
  }

  function validCandidate(candidate) {
    if (!candidate || candidate.available !== true || candidate.hasOnlyDefaultVariant !== true) return false;
    if (candidate.productId === null || candidate.productId === undefined) return false;
    if (candidate.variantId === null || candidate.variantId === undefined) return false;
    if (!Array.isArray(candidate.contents) || candidate.contents.length < 2) return false;

    var seen = new Set();
    for (var index = 0; index < candidate.contents.length; index += 1) {
      var productId = candidate.contents[index] && candidate.contents[index].productId;
      if (productId === null || productId === undefined || seen.has(String(productId))) return false;
      seen.add(String(productId));
    }
    return true;
  }

  function eligibleLine(line) {
    return Boolean(
      line &&
        typeof line.key === 'string' &&
        line.key.length > 0 &&
        finiteNumber(line.quantity, 0) > 0 &&
        line.hasProperties !== true &&
        line.hasSellingPlan !== true &&
        line.hasOnlyDefaultVariant === true,
    );
  }

  function evaluateCandidate(candidate, cartLines) {
    if (!validCandidate(candidate) || !Array.isArray(cartLines)) return null;
    if (cartLines.some(function (line) { return line && finiteNumber(line.quantity, 0) > 0 && sameId(line.productId, candidate.productId); })) return null;

    var eligible = cartLines.filter(eligibleLine);
    var totals = new Map();
    candidate.contents.forEach(function (item) { totals.set(String(item.productId), 0); });
    eligible.forEach(function (line) {
      var id = String(line.productId);
      if (totals.has(id)) totals.set(id, totals.get(id) + Math.floor(finiteNumber(line.quantity, 0)));
    });

    var presentCount = 0;
    var missing = [];
    candidate.contents.forEach(function (item) {
      if (totals.get(String(item.productId)) > 0) presentCount += 1;
      else missing.push(item);
    });

    var kind;
    var boxCount;
    if (missing.length === 0) {
      kind = 'full';
      boxCount = Math.min.apply(null, Array.from(totals.values()));
    } else if (missing.length <= 2 && presentCount >= 2) {
      kind = 'near';
      boxCount = 1;
    } else {
      return null;
    }
    if (!Number.isFinite(boxCount) || boxCount < 1) return null;

    var needed = new Map();
    candidate.contents.forEach(function (item) {
      var id = String(item.productId);
      needed.set(id, kind === 'full' || totals.get(id) > 0 ? boxCount : 0);
    });

    var allocations = [];
    eligible.forEach(function (line, cartOrder) {
      var id = String(line.productId);
      var remainingNeed = needed.get(id) || 0;
      if (remainingNeed <= 0) return;
      var lineQuantity = Math.floor(finiteNumber(line.quantity, 0));
      var quantity = Math.min(lineQuantity, remainingNeed);
      allocations.push({
        key: line.key,
        quantity: quantity,
        remainingQuantity: lineQuantity - quantity,
        cartOrder: cartOrder,
        productId: line.productId,
      });
      needed.set(id, remainingNeed - quantity);
    });

    if (Array.from(needed.values()).some(function (quantity) { return quantity > 0; })) return null;

    var consumedValue = 0;
    var savingKnown = true;
    allocations.forEach(function (allocation) {
      var source = eligible.find(function (line) { return line.key === allocation.key; });
      consumedValue += Math.max(0, finiteNumber(source && source.finalPrice, 0)) * allocation.quantity;
      if (source && (source.hasDiscount === true || source.hasVolumePricing === true)) savingKnown = false;
    });
    var targetCost = Math.max(0, finiteNumber(candidate.price, 0)) * boxCount;
    var saving = savingKnown ? Math.max(0, consumedValue - targetCost) : 0;

    return {
      candidate: candidate,
      kind: kind,
      boxCount: boxCount,
      presentCount: presentCount,
      missingCount: missing.length,
      missing: missing,
      allocations: allocations,
      consumedValue: consumedValue,
      targetCost: targetCost,
      delta: targetCost - consumedValue,
      savingKnown: savingKnown,
      saving: saving,
      settingsIndex: finiteNumber(candidate.settingsIndex, Number.MAX_SAFE_INTEGER),
    };
  }

  function selectOffer(offers) {
    if (!Array.isArray(offers) || offers.length === 0) return null;
    var ranked = offers.filter(Boolean).slice();
    ranked.sort(function (left, right) {
      var kind = (right.kind === 'full' ? 2 : 1) - (left.kind === 'full' ? 2 : 1);
      if (kind) return kind;
      var present = finiteNumber(right.presentCount, 0) - finiteNumber(left.presentCount, 0);
      if (present) return present;
      var rightSaving = right.savingKnown === true ? Math.max(0, finiteNumber(right.saving, 0)) : 0;
      var leftSaving = left.savingKnown === true ? Math.max(0, finiteNumber(left.saving, 0)) : 0;
      if (rightSaving !== leftSaving) return rightSaving - leftSaving;
      return finiteNumber(left.settingsIndex, Number.MAX_SAFE_INTEGER) - finiteNumber(right.settingsIndex, Number.MAX_SAFE_INTEGER);
    });
    return ranked[0] || null;
  }

  function buildUpdates(allocations) {
    if (!Array.isArray(allocations)) return {};
    var updates = {};
    allocations.forEach(function (allocation) {
      if (!allocation || typeof allocation.key !== 'string' || allocation.key.length === 0) throw new Error('Invalid allocation key');
      if (Object.prototype.hasOwnProperty.call(updates, allocation.key)) throw new Error('Duplicate allocation key');
      updates[allocation.key] = Math.max(0, Math.floor(finiteNumber(allocation.remainingQuantity, 0)));
    });
    return updates;
  }

  function variantQuantity(cart, variantId) {
    if (!cart || !Array.isArray(cart.items) || variantId === null || variantId === undefined) return null;
    return cart.items.reduce(function (sum, item) {
      var itemVariantId = item && (item.variant_id !== undefined ? item.variant_id : item.variantId);
      return sameId(itemVariantId, variantId) ? sum + Math.max(0, finiteNumber(item.quantity, 0)) : sum;
    }, 0);
  }

  function classifyReconciliation(beforeCart, afterCart, variantId, requestedQuantity) {
    var before = variantQuantity(beforeCart, variantId);
    var after = variantQuantity(afterCart, variantId);
    var requested = Math.floor(finiteNumber(requestedQuantity, 0));
    if (before === null || after === null || requested < 1) return { status: 'unexpected', addedQuantity: 0 };

    var added = after - before;
    var status = 'unexpected';
    if (added === 0) status = 'none';
    else if (added > 0 && added < requested) status = 'partial';
    else if (added === requested) status = 'full';
    return { status: status, addedQuantity: added };
  }

  return {
    deriveDeliveryState: deriveDeliveryState,
    evaluateCandidate: evaluateCandidate,
    selectOffer: selectOffer,
    buildUpdates: buildUpdates,
    classifyReconciliation: classifyReconciliation,
  };
});
